import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Popover, Spin, Tag, Typography, Tooltip } from 'antd';
import { BellOutlined, CheckOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  fetchNotificationInbox,
  markAllNotificationsRead,
  markNotificationRead,
} from '../services/notificationInboxService';
import { useAuth } from '../contexts/AuthContext';
import useLiveNotifications, { HISTORY_KEY, INBOX_KEY } from '../hooks/useLiveNotifications';
import { categoryMeta } from '../constants/notificationCategories';

dayjs.extend(relativeTime);

const { Text } = Typography;

/** Live connection up: a slow safety refresh only. Down or not configured: poll every minute. */
const SAFETY_REFRESH_MS = 10 * 60 * 1000;
const FALLBACK_POLL_MS = 60 * 1000;

function getAgeLabel(item) {
  if (item.due_at) {
    const due = dayjs(item.due_at);
    const today = dayjs().startOf('day');
    if (due.isBefore(today, 'day')) return `${today.diff(due, 'day')}d overdue`;
    if (due.isSame(today, 'day')) return 'Due today';
    return due.format('DD MMM');
  }
  if (item.created_at) return dayjs(item.created_at).fromNow();
  return '';
}

/** Mark notifications read in the cached inbox straight away, before the server answers. */
function markReadInCache(inbox, ids) {
  if (!inbox) return inbox;
  let changed = 0;
  const counts = { ...(inbox.counts || {}) };
  const items = inbox.items.map((item) => {
    if (item.is_read || (ids !== 'all' && !ids.includes(item.id))) return item;
    changed += 1;
    counts[item.category] = Math.max(0, (counts[item.category] || 0) - 1);
    return { ...item, is_read: true };
  });
  if (ids === 'all') {
    return { ...inbox, items, unread_count: 0, counts: { unread_total: 0 } };
  }
  const unread = Math.max(0, (inbox.unread_count || 0) - changed);
  return { ...inbox, items, unread_count: unread, counts: { ...counts, unread_total: unread } };
}

export default function NotificationCenterBell({ disabled }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const liveStatus = useLiveNotifications(disabled ? null : user?.id);
  const isLive = liveStatus === 'connected';
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  );

  useEffect(() => {
    const media = window.matchMedia('(max-width: 767px)');
    const onChange = (event) => setIsMobile(event.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  // Loaded once; after that live pushes keep it current. Polling only while live updates are down.
  const { data, isLoading } = useQuery({
    queryKey: INBOX_KEY,
    queryFn: () => fetchNotificationInbox(40),
    enabled: !disabled,
    refetchInterval: isLive ? SAFETY_REFRESH_MS : FALLBACK_POLL_MS,
    refetchOnWindowFocus: !isLive,
    staleTime: isLive ? SAFETY_REFRESH_MS : 30 * 1000,
  });

  const markRead = useMutation({
    mutationFn: markNotificationRead,
    onMutate: (id) => queryClient.setQueryData(INBOX_KEY, (inbox) => markReadInCache(inbox, [id])),
    onSettled: () => queryClient.invalidateQueries({ queryKey: HISTORY_KEY }),
  });

  const markAll = useMutation({
    mutationFn: markAllNotificationsRead,
    onMutate: () => queryClient.setQueryData(INBOX_KEY, (inbox) => markReadInCache(inbox, 'all')),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: INBOX_KEY });
      queryClient.invalidateQueries({ queryKey: HISTORY_KEY });
    },
  });

  const unreadCount = data?.unread_count ?? 0;
  const items = useMemo(() => data?.items ?? [], [data]);
  const counts = data?.counts ?? {};
  const unreadItems = items.filter((item) => !item.is_read);
  // The bell shows what still needs attention; read ones live on the notifications page.
  const shown = unreadItems.length ? unreadItems : items.slice(0, 8);

  const grouped = useMemo(() => {
    const groups = {};
    shown.forEach((item) => {
      const key = item.category || 'system';
      (groups[key] = groups[key] || []).push(item);
    });
    return groups;
  }, [shown]);

  const openItem = (item) => {
    if (!item.is_read) markRead.mutate(item.id);
    setOpen(false);
    if (item.url) navigate(item.url, item.url_state ? { state: item.url_state } : undefined);
  };

  const viewAll = () => {
    setOpen(false);
    navigate('/notifications');
  };

  const popoverContent = (
    <div className="nc-bell-popover">
      <div className="nc-bell-popover-head">
        <div>
          <Text strong>Notifications</Text>
          <Text type="secondary" className="nc-bell-popover-count">
            {unreadCount} unread
            {!isLive ? <Tooltip title="Live updates are not connected; checking every minute instead."> · not live</Tooltip> : null}
          </Text>
        </div>
        <Button type="link" size="small" onClick={() => markAll.mutate()} loading={markAll.isPending} disabled={unreadCount === 0}>
          Mark all read
        </Button>
      </div>

      {unreadCount > 0 && (
        <div className="nc-bell-categories">
          {Object.entries(counts)
            .filter(([key, value]) => key !== 'unread_total' && value > 0)
            .map(([key, value]) => (
              <Tag key={key} color={categoryMeta(key).color}>{value} {categoryMeta(key).label.toLowerCase()}</Tag>
            ))}
        </div>
      )}

      {isLoading && items.length === 0 ? (
        <div className="nc-bell-popover-loading"><Spin size="small" /></div>
      ) : shown.length === 0 ? (
        <div className="nc-bell-popover-empty">Nothing needs your attention right now</div>
      ) : (
        <div className="nc-bell-sections">
          {!unreadItems.length ? <Text type="secondary" style={{ fontSize: 12, padding: '0 12px' }}>All caught up. Recent:</Text> : null}
          {Object.entries(grouped).map(([category, categoryItems]) => {
            const meta = categoryMeta(category);
            return (
              <div key={category} className="nc-bell-section">
                <div className="nc-bell-section-title">
                  {meta.icon}
                  <span>{meta.label}</span>
                  <Tag>{categoryItems.length}</Tag>
                </div>
                <ul className="nc-bell-popover-list">
                  {categoryItems.map((item) => (
                    <li key={item.id}>
                      <div
                        className={`nc-bell-popover-item${item.priority === 'high' || item.priority === 'critical' ? ' is-urgent' : ''}${item.is_read ? ' is-read' : ''}`}
                        onClick={() => openItem(item)}
                        onKeyDown={(event) => event.key === 'Enter' && openItem(item)}
                        role="button"
                        tabIndex={0}
                      >
                        {!item.is_read && <span className="nc-bell-unread-dot" title="Unread" />}
                        <span className="nc-bell-popover-body">
                          <span className="nc-bell-popover-title">{item.title}</span>
                          <span className="nc-bell-popover-meta">{item.message}</span>
                        </span>
                        <span className="nc-bell-popover-age">{getAgeLabel(item)}</span>
                        {!item.is_read && (
                          <Tooltip title="Mark as read">
                            <Button
                              type="text"
                              size="small"
                              icon={<CheckOutlined />}
                              className="nc-bell-mark-btn"
                              onClick={(event) => {
                                event.stopPropagation();
                                markRead.mutate(item.id);
                              }}
                            />
                          </Tooltip>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      <div className="nc-bell-popover-footers">
        <button type="button" className="nc-bell-popover-footer" onClick={viewAll}>
          View all notifications
        </button>
      </div>
    </div>
  );

  return (
    <Popover
      content={popoverContent}
      title={null}
      trigger="click"
      placement={isMobile ? 'bottom' : 'bottomRight'}
      open={open}
      onOpenChange={(next) => !disabled && setOpen(next)}
      overlayClassName={`nc-bell-popover-overlay${isMobile ? ' nc-bell-popover-overlay--mobile' : ''}`}
      getPopupContainer={() => document.body}
      zIndex={1200}
      overlayStyle={isMobile ? { width: 'calc(100vw - 16px)', maxWidth: 'calc(100vw - 16px)' } : undefined}
    >
      <Badge count={unreadCount} size="small" overflowCount={99} offset={[-4, 4]}>
        <Button
          type="text"
          icon={<BellOutlined />}
          className="header-icon-btn header-alert-bell"
          title="Notifications"
          aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
          disabled={disabled}
        />
      </Badge>
    </Popover>
  );
}
